import { useMemo, useState } from "react";
import { format, parseISO, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useAdminMonthly } from "@/hooks/use-admin-queries";
import { MonthlyAttendanceTable } from "@/components/attendance/monthly-attendance-table";
import { StatusLegend } from "@/components/attendance/attendance-cell";
import { PersonMonthlyDrilldown } from "@/components/attendance/person-monthly-drilldown";
import { ReportDownloadButtons } from "@/components/reports/report-download-buttons";
import { buildPeriodReport } from "@/lib/reports/build-report";
import { Button } from "@/components/ui/button";
import { todayIST } from "@/lib/timezone";
import { adminApi } from "@/lib/api/admin";
import { getAdminPassword } from "@/lib/auth/storage";
import {
  orderedAdminTables,
  tableShowsShifts,
  tableSubtitle,
} from "@/lib/admin-tables";
import type { MonthlyRow } from "@/lib/types";

function monthKey(d: Date): string {
  return format(d, "yyyy-MM");
}

export default function MonthlyAttendancePage() {
  const [cursor, setCursor] = useState(() =>
    startOfMonth(parseISO(todayIST()))
  );
  const [drilldown, setDrilldown] = useState<{
    row: MonthlyRow;
    tableLabel: string;
  } | null>(null);
  const month = monthKey(cursor);
  const adminPassword = getAdminPassword();
  const { data, isLoading, error } = useAdminMonthly(month);

  const year = cursor.getFullYear();
  const monthIndex = cursor.getMonth();
  const tables = useMemo(
    () => (data ? orderedAdminTables(data.tables) : []),
    [data]
  );

  async function handleExport() {
    if (!adminPassword) return;
    const res = await adminApi.exportMonthlyCsv(adminPassword, month);
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Monthly Attendance</h2>
          <p className="text-sm text-muted-foreground">
            Separate tables per group — a person in Employee + PDC appears in
            both. Presence % = P ÷ (P+A+L). MS/FS is a label only — late and
            absent still count. O, S, TP excluded.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-1 h-4 w-4" />
            Export Excel (CSV)
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCursor(new Date(year, monthIndex - 1, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="min-w-[140px] text-center font-medium">
            {format(cursor, "MMMM yyyy")}
          </p>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCursor(new Date(year, monthIndex + 1, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        <StatusLegend />
        <p className="text-xs text-muted-foreground">
          % = P ÷ (P+A+L) — MS/FS still count as late or absent; O, S, TP excluded
        </p>
      </div>

      {isLoading && (
        <p className="text-muted-foreground">Loading monthly attendance…</p>
      )}
      {error && (
        <p className="text-red-600">Could not load monthly attendance.</p>
      )}

      {data && (
        <div className="space-y-10">
          {tables.map((table) => {
            const report = buildPeriodReport(data, data.dates, {
              period: "monthly",
              tableKey: table.key,
              title: `Monthly Presence — ${table.label}`,
              presenceHeader: "Monthly Presence",
            });
            return (
              <div key={table.key} className="space-y-2">
                <div className="flex justify-end">
                  <ReportDownloadButtons
                    report={report}
                    filenameBase={`monthly-presence-${month}-${table.key}`}
                    disabled={table.rows.length === 0}
                  />
                </div>
                <MonthlyAttendanceTable
                  title={table.label}
                  subtitle={tableSubtitle(table.key)}
                  dates={data.dates}
                  rows={table.rows}
                  showShifts={tableShowsShifts(table.key)}
                  onRowClick={(row) =>
                    setDrilldown({
                      row,
                      tableLabel: table.label,
                    })
                  }
                />
              </div>
            );
          })}
        </div>
      )}

      <PersonMonthlyDrilldown
        open={drilldown !== null}
        onClose={() => setDrilldown(null)}
        row={drilldown?.row ?? null}
        tableLabel={drilldown?.tableLabel ?? ""}
        dates={data?.dates ?? []}
      />
    </div>
  );
}
